import {
  Component, EventEmitter, Input, Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BadgeComponent } from '../../../../shared/components/ui/badge/badge.component';
import { ConfirmationModalComponent } from '../../../../shared/components/ui/confirmation-modal/confirmation-modal.component';
import { FormStatusPipe } from '../../pipes/form-status.pipe';
import { FormTypeLabelPipe } from '../../pipes/form-type-label.pipe';
import { FormListModel, FormStatus } from '../../models/form.model';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { Router } from '@angular/router';
import { DisableWithoutPermissionDirective } from '../../../../shared/directives/disable-without-permission.directive';

export type LifecycleAction = 'publish' | 'pause' | 'resume' | 'archive' | 'restore';

@Component({
  selector: 'app-forms-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BadgeComponent,
    ConfirmationModalComponent,
    FormStatusPipe,
    FormTypeLabelPipe,
    DisableWithoutPermissionDirective,
  ],
  templateUrl: './forms-table.component.html',
})
export class FormsTableComponent {
  @Input() forms: FormListModel[] = [];
  @Input() loading = false;

  // Paginación
  @Input() totalElements = 0;
  @Input() totalPages = 0;
  @Input() currentPage = 0;
  @Input() pageSize = 10;
  @Input() pageSizeOptions: number[] = [5, 10, 20, 50];
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  @Output() openDetail = new EventEmitter<FormListModel>();
  @Output() openEditor = new EventEmitter<number>();
  @Output() openSubmissions = new EventEmitter<number>();
  @Output() deleteForms = new EventEmitter<number[]>();
  @Output() duplicateForm = new EventEmitter<number>();
  // inc27: acción de ciclo de vida coherente por estado.
  @Output() statusAction = new EventEmitter<{ id: number; action: LifecycleAction }>();

  get hasPrevPage(): boolean { return this.currentPage > 0; }
  get hasNextPage(): boolean { return this.currentPage < this.totalPages - 1; }
  get pageDisplay(): number { return this.currentPage + 1; }
  get pagesArray(): number[] { return Array.from({ length: this.totalPages }, (_, i) => i + 1); }

  selected = new Set<number>();
  deleteConfirm = { isOpen: false, ids: [] as number[] };

  get isAllSelected(): boolean {
    return this.forms.length > 0 && this.forms.every(f => this.selected.has(f.id));
  }

  toggleAll(event: Event): void {
    (event.target as HTMLInputElement).checked
      ? this.forms.forEach(f => this.selected.add(f.id))
      : this.forms.forEach(f => this.selected.delete(f.id));
  }

  toggleRow(id: number, event: Event): void {
    event.stopPropagation();
    this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id);
  }

  clearSelection(): void {
    this.selected.clear();
  }

  confirmDeleteSelected(): void {
    this.deleteConfirm = { isOpen: true, ids: Array.from(this.selected) };
  }

  confirmDeleteOne(id: number, event: Event): void {
    event.stopPropagation();
    this.deleteConfirm = { isOpen: true, ids: [id] };
  }

  onDeleteConfirmed(): void {
    this.deleteForms.emit(this.deleteConfirm.ids);
    this.selected.clear();
    this.deleteConfirm = { isOpen: false, ids: [] };
  }

  onDeleteCancelled(): void {
    this.deleteConfirm = { isOpen: false, ids: [] };
  }

  // inc27: acciones coherentes por estado (un formulario no puede estar
  // publicado y archivado a la vez).
  //  DRAFT     → Publicar, Archivar
  //  PUBLISHED → Pausar, Archivar
  //  PAUSED    → Reanudar, Archivar
  //  ARCHIVED  → Restaurar
  actionsFor(status: FormStatus): { action: LifecycleAction; label: string; icon: 'publish' | 'pause' | 'resume' | 'archive' | 'restore'; danger?: boolean }[] {
    switch (status) {
      case 'DRAFT':
        return [
          { action: 'publish', label: 'Publicar', icon: 'publish' },
          { action: 'archive', label: 'Archivar', icon: 'archive' },
        ];
      case 'PUBLISHED':
        return [
          { action: 'pause', label: 'Pausar', icon: 'pause' },
          { action: 'archive', label: 'Archivar', icon: 'archive' },
        ];
      case 'PAUSED':
        return [
          { action: 'resume', label: 'Reanudar', icon: 'resume' },
          { action: 'archive', label: 'Archivar', icon: 'archive' },
        ];
      case 'ARCHIVED':
        return [
          { action: 'restore', label: 'Restaurar', icon: 'restore' },
        ];
      default:
        return [];
    }
  }

  emitAction(id: number, action: LifecycleAction): void {
    this.statusAction.emit({ id, action });
  }

  constructor(private authService: AuthService, private router: Router) {}

  // Backend requiere Admin para DELETE de formularios.
  get isAdmin(): boolean {
    const role = this.authService.getCurrentUser()?.roleName ?? '';
    return /admin/i.test(role);
  }
}
